import {
  ModalHeader,
  ModalBody,
  ModalContent,
  ModalOverlay,
  VStack,
  Text,
  ModalCloseButton,
  Modal,
  HStack,
  Grid,
  GridItem,
  TableCaption,
  Table,
  Thead,
  Tr,
  Td,
  Tbody,
  ModalFooter,
  Button,
} from "@chakra-ui/react";
import * as yup from "yup";
import {
  createDialogContext,
  DialogProvider,
} from "../../../../../Common/Dialog";
import { useContext } from "react";
import { Select } from "chakra-react-select";
import { Controller, useForm } from "react-hook-form";
import ValidatedField from "./ValidatedField";
import WhiteBgInput from "./WhiteBgInput";
import { yupResolver } from "@hookform/resolvers/yup";

export const ColumnMatchingModalContext = createDialogContext();

const optionToLabel = ({
  value,
  label,
}: {
  label?: string;
  value?: string;
}) => {
  if (value === "FN") {
    return "First Name";
  }

  if (value === "LN") {
    return "Last Name";
  }

  if (value === "EMAIL") {
    return "Email";
  }

  if (value === "PHONE") {
    return "Phone";
  }

  if (value === "COUNTRY") {
    return "Country";
  }

  return label;
};

const ColumnMatchingModal = ({
  title,
  onSave,
  audienceColumns: {
    sample,
    schema_fields,
    saved_schema_fields = [],
    file_key,
    file_name,
    file_description,
  },
}: {
  audienceColumns: {
    sample: string[][];
    schema_fields: string[];
    saved_schema_fields: string[];
    file_key: string;
    file_name: string;
    file_description: string;
  };
  title: string;
  onSave: (data: {
    file_key: string;
    file_name: string;
    file_description: string;
    schema_fields: string[];
  }) => Promise<void>;
}) => {
  const { isOpen, close } = useContext(ColumnMatchingModalContext);
  const {
    control,
    reset,
    watch,
    register,
    setError,
    handleSubmit,
    formState: { errors, isValid, isDirty },
  } = useForm({
    mode: "onChange",
    criteriaMode: "all",
    reValidateMode: "onChange",
    resolver: yupResolver(
      yup.object({
        name: yup.string().required("Audience name is required"),
        schema: yup
          .array()
          .of(
            yup.object({
              label: yup.string().nullable(),
              value: yup.string(),
            })
          )
          .min(1, "At least one field must be mapped to Phone"),
      })
    ),
    defaultValues: {
      name: file_name,
      schema:
        saved_schema_fields.map((c) => ({
          label: c,
          value: c,
        })) || [],
    },
  });

  const _onSubmit = (data: {
    name: string;
    schema: { label: string; value: string }[];
  }) => {
    if (!data.schema.some((c) => c.value === "PHONE")) {
      setError("schema", {
        message: "Phone number is required",
      });
      return;
    }
    return onSave({
      file_key,
      file_name: data.name,
      file_description,
      schema_fields: data.schema.map((c) => c.value),
    }).then(() => reset());
  };

  const fieldsUsed = watch("schema").map((c) => c?.value);
  const hasPhone = watch("schema").some((c) => c?.value === "PHONE");

  return (
    <Modal
      size={["initial", "full"]}
      isOpen={isOpen}
      onClose={close}
      isCentered
    >
      <ModalOverlay />
      <ModalContent
        mb={[0, "auto"]}
        w="full"
        borderBottomLeftRadius={[0, "md"]}
        borderBottomRightRadius={[0, "md"]}
      >
        <ModalHeader paddingTop={4}>
          <VStack gap={0} width={"full"}>
            <Text w="full">{title}</Text>
          </VStack>
        </ModalHeader>
        <ModalCloseButton onClick={close} />
        <ModalBody textAlign={"center"} w="full" minW={["full", 600]}>
          <VStack w="full" spacing={8}>
            <ValidatedField name="file_name" errors={errors}>
              <Text w="full" textAlign={"left"}>
                Audience name
              </Text>
              <WhiteBgInput placeholder="Audience name" {...register("name")} />
            </ValidatedField>
            <Text w="full" textAlign={"left"}>
              Please match the column headers to the content of the rows.
            </Text>
            {!hasPhone ? (
              <Text w="full" textAlign={"left"} color="red.200" fontSize="sm">
                At least the phone number is required
              </Text>
            ) : null}
            <Table layout="fixed">
              <Thead>
                <ValidatedField name="schema" errors={errors} as={Tr}>
                  {sample.at(0)?.map((column, index) => (
                    <Td
                      key={column}
                      width={`${100 / schema_fields.length}%`}
                      padding={1}
                    >
                      <Controller
                        key={column}
                        control={control}
                        name={`schema.${index}`}
                        render={({ field }) => (
                          <Select
                            isDisabled={saved_schema_fields.length > 0}
                            chakraStyles={{
                              container: () => ({
                                position: "relative",
                                width: "100%",
                              }),
                            }}
                            isClearable
                            {...field}
                            formatOptionLabel={(option) =>
                              optionToLabel(option)
                            }
                            // @ts-ignore
                            options={schema_fields
                              .filter((c) => !fieldsUsed.includes(c))
                              .map((c) => ({
                                label: c,
                                value: c,
                              }))}
                            onChange={field.onChange}
                          />
                        )}
                      />
                    </Td>
                  ))}
                </ValidatedField>
              </Thead>
              <Tbody>
                {sample.map((row, rowIndex) => (
                  <Tr key={rowIndex}>
                    {row.map((r, cellIndex) => (
                      <Td
                        key={`${rowIndex}-${cellIndex}`}
                        width={`${100 / schema_fields.length}%`}
                      >
                        <Text>{r}</Text>
                      </Td>
                    ))}
                  </Tr>
                ))}
                <Tr>
                  <Td colSpan={schema_fields.length}>
                    <Text color="gray.400" w="full" textAlign={"center"}>
                      Displaying a sample of {Math.min(10, sample.length)} rows.
                    </Text>
                  </Td>
                </Tr>
              </Tbody>
            </Table>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack>
            <Button onClick={close}>Cancel</Button>
            {saved_schema_fields.length > 0 ? null : (
              <Button
                isDisabled={!isValid}
                variant="kydDark"
                onClick={handleSubmit(_onSubmit)}
              >
                Save
              </Button>
            )}
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export const withColumnMatchingModal =
  <T extends object>(Component: React.ComponentType<T>) =>
  (props: T) => {
    return (
      <DialogProvider context={ColumnMatchingModalContext}>
        <Component {...(props as T)} />
      </DialogProvider>
    );
  };

export default ColumnMatchingModal;
